// Render an Alert component to inform the assigner user that some Operator(s) have a time conflict in their new assignments
import React from 'react';

import { InformationCircleIcon } from '@heroicons/react/24/outline';

import SpinnerCircle from '@/components/Spinner/SpinnerCircle';

import { useEditSchedulerContext } from '@/context/EditSchedulerContext';

import { JobAssignmentDoc } from '@isd/lib/firebase/db/metaTypes';

import { reactAppJobDetailsRoute, reactAppUrl } from '@/env';
import { replaceParamsInRoute } from '@isd/lib/helpers/functions';

export default function AssignmentsConflictsPanel() {
  const { conflicts, isCheckingConflicts, onSelectOperatorToRemove } =
    useEditSchedulerContext();

  if (isCheckingConflicts) {
    return (
      <div className="flex justify-center py-2">
        <SpinnerCircle />
      </div>
    );
  }

  if (conflicts.length === 0) {
    return null;
  }

  return (
    <div
      className="alert alert-soft alert-warning items-start justify-center shadow-lg"
      role="alert"
    >
      <div className="flex flex-col items-center">
        <div className="flex items-center">
          <InformationCircleIcon className="size-6" />
          <span className="ml-2 font-bold">Time Conflicts Detected:</span>
        </div>
        <ul className="max-h-[25vh] list-disc overflow-y-auto pl-10">
          {conflicts.map(
            ({ operation, overlappingAssignments, jobDoc }, index) => {
              const operatorNickname =
                operation.jobAssignmentDoc?.get('receiver.user.nickname') ||
                'An operator';
              const newJobName =
                operation.jobAssignmentDoc?.get('job.name') || 'a job';
              return overlappingAssignments.map((assignment) => {
                const jobName = assignment.get('job.name');
                const jobId = assignment.get('job.id') || '';
                const jobDetailsUrl = `${reactAppUrl}${replaceParamsInRoute(reactAppJobDetailsRoute, { jobId })}`;
                return (
                  <li key={`${index}-${assignment.id}`}>
                    <span className="flex items-center gap-2">
                      <span>
                        {operatorNickname} is being assigned to{' '}
                        <b>{newJobName}</b>, but is already assigned to{' '}
                        <a
                          href={jobDetailsUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="link"
                        >
                          {jobName}
                        </a>
                      </span>
                      {operation.jobAssignmentDoc && (
                        <button
                          className="btn btn-xs btn-warning btn-soft"
                          onClick={() =>
                            onSelectOperatorToRemove(
                              operation.jobAssignmentDoc as JobAssignmentDoc,
                              jobDoc
                            )
                          }
                        >
                          Remove
                        </button>
                      )}
                    </span>
                  </li>
                );
              });
            }
          )}
        </ul>
        <span className="mt-1 self-center">
          Please review the assignments.
        </span>{' '}
      </div>
    </div>
  );
}
